// @ts-nocheck
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Wallet, Ticket, ShoppingBag, X, CheckCircle, ChevronDown, ChevronUp, Calendar, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import QRCode from 'react-qr-code';
import { parseLocalDate } from '@/utils/formatters';
import NotAvailableYet from '../components/NotAvailableYet';

interface MyWalletScreenProps {
  userId: string;
  profileId?: string;
  onNavigateToEvents: () => void;
}

interface TicketData {
  id: string;
  ticket_name: string;
  ticket_price: number;
  qr_code: string;
  is_redeemed: boolean;
  redeemed_at: string | null;
  event_id: string;
  events: {
    id: string;
    name: string;
    start_date: string;
    end_date: string | null;
    thumbnail_url: string | null;
  } | null;
}

interface ExtraData {
  id: string;
  quantity: number;
  event_id: string;
  event_extras: {
    name: string;
    price: number;
    thumbnail_url: string | null;
  } | null;
  events: {
    name: string;
    start_date: string;
  } | null;
}

interface GroupedEvent {
  eventId: string;
  eventName: string;
  startDate: string;
  endDate: string | null;
  thumbnailUrl: string | null;
  tickets: TicketData[];
}

const FEATURED_STORAGE_URL = 'https://kvttsygfutwcywroraji.supabase.co/storage/v1/object/public/event-assets';
const EXTRAS_STORAGE_URL = 'https://kvttsygfutwcywroraji.supabase.co/storage/v1/object/public/event-assets';

type WalletTab = 'tickets' | 'extras';

const MyWalletScreen: React.FC<MyWalletScreenProps> = ({ userId, profileId, onNavigateToEvents }) => {
  const [activeTab, setActiveTab] = useState<WalletTab>('tickets');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null);

  // Fetch tickets
  const { data: tickets, isLoading: ticketsLoading } = useQuery({
    queryKey: ['my-purchased-tickets', userId],
    queryFn: async (): Promise<TicketData[]> => {
      if (!userId) return [];

      // Spectator tickets are not in RLE yet — the tab shows Not available yet.
      return [];
    },
    enabled: !!userId,
  });

  // Fetch extras
  const { data: extras, isLoading: extrasLoading } = useQuery({
    queryKey: ['my-purchased-extras', userId],
    queryFn: async (): Promise<ExtraData[]> => {
      if (!userId) return [];

      const { data: extrasData, error: extrasError } = await supabase
        .from('purchased_extras')
        .select('id, event_id, extra_name, extra_price, player_name, events(name, start_date)')
        .eq('user_id', userId);

      if (extrasError) {
        console.error('[MyWallet] Error fetching extras:', extrasError);
        throw extrasError;
      }

      return (extrasData || []).map((x: any) => ({
        id: x.id,
        quantity: 1,
        event_id: x.event_id,
        event_extras: { name: x.player_name ? `${x.extra_name} — ${x.player_name}` : x.extra_name, price: Math.round((Number(x.extra_price) || 0) * 100), thumbnail_url: null },
        events: x.events,
      })) as ExtraData[];
    },
    enabled: !!userId,
  });

  // Group tickets by event
  const groupedByEvent: GroupedEvent[] = React.useMemo(() => {
    if (!tickets || tickets.length === 0) return [];

    const eventMap = new Map<string, GroupedEvent>();

    tickets.forEach((ticket) => {
      const eventId = ticket.event_id;
      if (!eventId) return;

      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, {
          eventId,
          eventName: ticket.events?.name || 'Unknown Event',
          startDate: ticket.events?.start_date || '',
          endDate: ticket.events?.end_date || null,
          thumbnailUrl: ticket.events?.thumbnail_url || null,
          tickets: [],
        });
      }

      eventMap.get(eventId)!.tickets.push(ticket);
    });

    return Array.from(eventMap.values()).sort((a, b) => 
      parseLocalDate(b.startDate).getTime() - parseLocalDate(a.startDate).getTime()
    );
  }, [tickets]);

  const toggleExpanded = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const formatEventDates = (startDate: string, endDate: string | null) => {
    const start = parseLocalDate(startDate);
    if (!endDate) {
      return format(start, 'EEEE, MMMM d, yyyy');
    }
    const end = parseLocalDate(endDate);
    if (start.toDateString() === end.toDateString()) {
      return format(start, 'EEEE, MMMM d, yyyy');
    }
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(priceInCents / 100);
  };

  if (selectedTicket) {
    return (
      <TicketQRCodeView
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    );
  }

  const isLoading = activeTab === 'tickets' ? ticketsLoading : extrasLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center px-4 h-14 gap-2">
          <Wallet className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">My Wallet</h1>
        </div>

        {/* Segment Toggle */}
        <div className="flex px-4 pb-3 gap-2">
          <button
            onClick={() => setActiveTab('tickets')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-colors",
              activeTab === 'tickets'
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            <Ticket className="h-4 w-4" />
            Tickets
            {tickets && tickets.length > 0 && (
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full",
                activeTab === 'tickets' ? "bg-primary-foreground/20" : "bg-background"
              )}>
                {tickets.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('extras')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-colors",
              activeTab === 'extras'
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            <ShoppingBag className="h-4 w-4" />
            Extras
            {extras && extras.length > 0 && (
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full",
                activeTab === 'extras' ? "bg-primary-foreground/20" : "bg-background"
              )}>
                {extras.length}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : activeTab === 'tickets' ? (
          // Tickets Tab
          groupedByEvent.length > 0 ? (
            <div className="p-4 space-y-4">
              {groupedByEvent.map((group) => {
                const isExpanded = expandedEvents.has(group.eventId);
                const thumbnailSrc = group.thumbnailUrl
                  ? `${FEATURED_STORAGE_URL}/${group.thumbnailUrl}`
                  : null;

                return (
                  <div key={group.eventId} className="bg-card rounded-xl border border-border overflow-hidden">
                    {/* Event Header - Clickable */}
                    <button
                      onClick={() => toggleExpanded(group.eventId)}
                      className="w-full text-left"
                    >
                      <div className="flex gap-3 p-3">
                        {/* Thumbnail */}
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          {thumbnailSrc ? (
                            <img
                              src={thumbnailSrc}
                              alt={group.eventName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Calendar className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        {/* Event Info */}
                        <div className="flex-1 min-w-0">
                          <h2 className="font-semibold text-foreground truncate">
                            {group.eventName}
                          </h2>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {formatEventDates(group.startDate, group.endDate)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              <Ticket className="h-3 w-3" />
                              {group.tickets.length} ticket{group.tickets.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Expand/Collapse Icon */}
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded Tickets */}
                    {isExpanded && (
                      <div className="border-t border-border bg-muted/30">
                        <div className="p-3 space-y-2">
                          {group.tickets.map((ticket) => (
                            <button
                              key={ticket.id}
                              onClick={() => setSelectedTicket(ticket)}
                              className={cn(
                                "w-full bg-background rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50",
                                ticket.is_redeemed && "opacity-60"
                              )}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-medium">{ticket.ticket_name}</h3>
                                    {ticket.is_redeemed ? (
                                      <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                        <CheckCircle className="h-3 w-3" />
                                        USED
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full">
                                        Valid
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-0.5">
                                    {formatPrice(ticket.ticket_price)}
                                  </p>
                                </div>

                                {/* Mini QR Preview */}
                                <div className="bg-white p-1.5 rounded">
                                  <QRCode
                                    value={ticket.qr_code}
                                    size={40}
                                    level="L"
                                  />
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <NotAvailableYet message="Spectator tickets are not available in the app yet." />
          )
        ) : (
          // Extras Tab
          extras && extras.length > 0 ? (
            <div className="p-4 space-y-3">
              {extras.map((extra) => {
                const thumbnailSrc = extra.event_extras?.thumbnail_url
                  ? `${EXTRAS_STORAGE_URL}/${extra.event_extras.thumbnail_url}`
                  : null;

                return (
                  <div key={extra.id} className="bg-card rounded-xl border border-border p-3">
                    <div className="flex gap-3">
                      {/* Thumbnail */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        {thumbnailSrc ? (
                          <img
                            src={thumbnailSrc}
                            alt={extra.event_extras?.name || 'Extra'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Extra Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground">
                          {extra.event_extras?.name || 'Unknown Extra'}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">
                          {extra.events?.name || 'Unknown Event'}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            Qty: {extra.quantity}
                          </span>
                          <span className="text-sm font-medium text-foreground">
                            {extra.event_extras?.price 
                              ? formatPrice(extra.event_extras.price)
                              : 'Free'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No extras purchased</h3>
              <p className="text-sm text-muted-foreground text-center">
                Extras like merchandise or add-ons will appear here
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Full screen QR code view
const TicketQRCodeView: React.FC<{ ticket: TicketData; onClose: () => void }> = ({
  ticket,
  onClose,
}) => {
  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(priceInCents / 100);
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 h-14 border-b border-border">
        <h1 className="text-lg font-semibold">Ticket</h1>
        <button onClick={onClose} className="p-2">
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Ticket Info */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold mb-1">{ticket.ticket_name}</h2>
          <p className="text-muted-foreground">{ticket.events?.name}</p>
          {ticket.events?.start_date && (
            <p className="text-sm text-muted-foreground mt-1">
              {format(parseLocalDate(ticket.events.start_date), 'EEEE, MMMM d, yyyy')}
            </p>
          )}
          <p className="text-lg font-semibold text-primary mt-2">
            {formatPrice(ticket.ticket_price)}
          </p>
        </div>

        {/* QR Code */}
        <div
          className={cn(
            "bg-white p-6 rounded-2xl shadow-lg",
            ticket.is_redeemed && "opacity-50"
          )}
        >
          <QRCode value={ticket.qr_code} size={256} level="H" />
        </div>

        {/* Status */}
        {ticket.is_redeemed ? (
          <div className="mt-6 flex items-center gap-2 text-muted-foreground">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">
              Redeemed{' '}
              {ticket.redeemed_at &&
                format(new Date(ticket.redeemed_at), 'MMM d, h:mm a')}
            </span>
          </div>
        ) : (
          <div className="mt-6">
            <span className="inline-flex items-center gap-1 text-sm bg-green-500/10 text-green-600 px-3 py-1 rounded-full font-medium">
              <CheckCircle className="h-4 w-4" />
              Valid
            </span>
            <p className="mt-3 text-sm text-muted-foreground text-center">
              Present this QR code at the entrance
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWalletScreen;
